import React, { useState } from 'react';
import { useUser } from '../../context/UserContext';
import { PlusIcon, TrashIcon } from 'lucide-react';
const ManageUsers = () => {
  const {
    users,
    addUser,
    deleteUser
  } = useUser();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);
    // Validation
    if (!name || !email || !password) {
      setError('All fields are required');
      setLoading(false);
      return;
    }
    if (users.some(u => u.email === email)) {
      setError('A user with this email already exists');
      setLoading(false);
      return;
    }
    try {
      // Add user
      await addUser({
        name,
        email,
        password,
        isAdmin
      });
      // Reset form
      setName('');
      setEmail('');
      setPassword('');
      setIsAdmin(false);
      // Show success message
      setSuccessMessage('User added successfully!');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError('Failed to add user. Please try again.');
      console.error('Add user error:', err);
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteUser = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(id);
      } catch (err) {
        setError('Failed to delete user. Please try again.');
        console.error('Delete user error:', err);
      }
    }
  };
  return <div data-id="element-240">
      <h3 className="text-xl font-semibold mb-6 text-gray-700" data-id="element-241">Manage APNs</h3>

      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" data-id="element-242">
          {error}
        </div>}

      {successMessage && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4" data-id="element-243">
          {successMessage}
        </div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-244">
        <div className="md:col-span-2" data-id="element-245">
          <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-246">
            <ul className="divide-y divide-gray-200" data-id="element-247">
              {users.map(user => <li key={user.id} data-id="element-248">
                  <div className="px-4 py-4 flex items-center justify-between sm:px-6" data-id="element-249">
                    <div data-id="element-250">
                      <h4 className="text-lg font-medium text-gray-900" data-id="element-251">
                        {user.name}
                      </h4>
                      <p className="text-sm text-gray-500" data-id="element-252">{user.email}</p>
                      {user.isAdmin && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1" data-id="element-253">
                          Admin
                        </span>}
                    </div>
                    <button onClick={() => handleDeleteUser(user.id)} className="text-red-600 hover:text-red-900" data-id="element-254">
                      <TrashIcon size={20} data-id="element-255" />
                    </button>
                  </div>
                </li>)}
            </ul>
          </div>
        </div>

        <div data-id="element-256">
          <div className="bg-gray-50 p-4 rounded-md shadow-sm border border-gray-200" data-id="element-257">
            <h4 className="text-lg font-medium text-gray-900 mb-4" data-id="element-258">
              Add New APN
            </h4>
            <form onSubmit={handleAddUser} data-id="element-259">
              <div className="space-y-4" data-id="element-260">
                <div data-id="element-261">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-262">
                    Name
                  </label>
                  <input type="text" id="name" value={name} onChange={e => setName(e.target.value)} disabled={loading} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50" data-id="element-263" />
                </div>
                <div data-id="element-264">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-265">
                    Email
                  </label>
                  <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} disabled={loading} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50" data-id="element-266" />
                </div>
                <div data-id="element-267">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700" data-id="element-268">
                    Password
                  </label>
                  <input type="password" id="password" value={password} onChange={e => setPassword(e.target.value)} disabled={loading} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50" data-id="element-269" />
                </div>
                <div className="flex items-center" data-id="element-270">
                  <input id="isAdmin" type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} disabled={loading} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50" data-id="element-271" />
                  <label htmlFor="isAdmin" className="ml-2 block text-sm text-gray-900" data-id="element-272">
                    Admin Access
                  </label>
                </div>
                <div data-id="element-273">
                  <button type="submit" disabled={loading} className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" style={{
                  backgroundColor: '#4772B0'
                }} onMouseEnter={e => !loading && (e.currentTarget.style.backgroundColor = '#EF5350')} onMouseLeave={e => !loading && (e.currentTarget.style.backgroundColor = '#4772B0')} data-id="element-274">
                    <PlusIcon size={16} className="mr-2" data-id="element-275" />
                    {loading ? 'Adding User...' : 'Add User'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>;
};
export default ManageUsers;